import jsPDF from 'jspdf';
import { getImageUrl } from '@/lib/imageUtils';

// ─── Types ───────────────────────────────────────────────────────────
export interface DocumentConfig {
  colorPrimario: string;   // hex e.g. "#333333"
  colorSecundario: string; // hex
  colorAcento: string;     // hex
  mostrarLogo: boolean;
  mostrarDatosEmpresa: boolean;
  textoPiePagina?: string;
  textoLegal?: string;
}

export interface EmpresaInfo {
  nombre: string;
  razon_social?: string | null;
  nif_cif?: string | null;
  direccion_calle?: string | null;
  direccion_numero?: string | null;
  codigo_postal?: string | null;
  ciudad?: string | null;
  provincia?: string | null;
  telefono?: string | null;
  email?: string | null;
  logo_url?: string | null;
  sello_url?: string | null;
  numero_dgp?: string | null;
  numero_reci?: string | null;
}

export interface SignatureData {
  label: string;
  imageData?: string | null; // base64 PNG
  nombre?: string;
  /** If true, when no imageData is provided, the company stamp/signature will be auto-applied. */
  esEmpresa?: boolean;
}

export interface FieldItem {
  label: string;
  value: string;
}

const DEFAULT_CONFIG: DocumentConfig = {
  colorPrimario: '#333333',
  colorSecundario: '#808080',
  colorAcento: '#3B82F6',
  mostrarLogo: true,
  mostrarDatosEmpresa: true,
};

// ─── Helpers ─────────────────────────────────────────────────────────
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

async function loadImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Sanitize text for jsPDF's default WinAnsi (cp1252) encoding.
 * Replaces Unicode characters that render as garbage symbols when using
 * the built-in helvetica/times/courier fonts (smart quotes, em-dashes,
 * ellipsis, non-breaking spaces, bullets, arrows, etc).
 */
export function sanitizePdfText(input: unknown): string {
  if (input === null || input === undefined) return '';
  let s = String(input);
  // Normalize accents to composed form so 'é' renders correctly
  try { s = s.normalize('NFC'); } catch {}
  const map: Record<string, string> = {
    '\u00A0': ' ',      // nbsp
    '\u202F': ' ',      // narrow nbsp
    '\u2009': ' ',      // thin space
    '\u2007': ' ',
    '\u2008': ' ',
    '\u200A': ' ',
    '\u200B': '',       // zero-width space
    '\u200C': '',
    '\u200D': '',
    '\uFEFF': '',
    '\u2010': '-',
    '\u2011': '-',
    '\u2012': '-',
    '\u2013': '-',      // en-dash
    '\u2014': '-',      // em-dash
    '\u2015': '-',
    '\u2018': "'",      // ‘
    '\u2019': "'",      // ’
    '\u201A': ',',
    '\u201B': "'",
    '\u201C': '"',      // “
    '\u201D': '"',      // ”
    '\u201E': '"',
    '\u2032': "'",
    '\u2033': '"',
    '\u2026': '...',    // …
    '\u2022': '-',      // •
    '\u00B7': '-',      // ·
    '\u2192': '->',
    '\u2190': '<-',
    '\u2194': '<->',
    '\u2713': 'OK',     // ✓
    '\u2714': 'OK',
    '\u2717': 'X',      // ✗
    '\u2718': 'X',
  };
  s = s.replace(/[\u00A0\u202F\u2007-\u200D\uFEFF\u2010-\u2015\u2018-\u201E\u2032\u2033\u2026\u2022\u00B7\u2190\u2192\u2194\u2713\u2714\u2717\u2718]/g, ch => map[ch] ?? ch);
  // Replace any remaining non-WinAnsi char (outside Latin-1 supplement) with '?'
  s = s.replace(/[^\x09\x0A\x0D\x20-\x7E\u00A1-\u00FF]/g, '?');
  return s;
}

// ─── Builder ─────────────────────────────────────────────────────────
export class PdfDocumentBuilder {
  private doc: jsPDF;
  private config: DocumentConfig;
  private empresa: EmpresaInfo | null;
  private logoBase64: string | null = null;
  private selloBase64: string | null = null;
  private margin = 15;
  private y = 15;
  private pageCount = 1;

  get currentY(): number { return this.y; }
  set currentY(val: number) { this.y = val; }
  get pdf(): jsPDF { return this.doc; }
  get pageWidth(): number { return this.doc.internal.pageSize.getWidth(); }
  get pageHeight(): number { return this.doc.internal.pageSize.getHeight(); }
  get contentWidth(): number { return this.pageWidth - this.margin * 2; }

  private constructor(config: DocumentConfig, empresa: EmpresaInfo | null, orientation: 'p' | 'l' = 'p') {
    this.doc = new jsPDF(orientation, 'mm', 'a4');
    this.config = config;
    this.empresa = empresa;
  }

  /** Factory: create builder pre-loaded with empresa logo */
  static async create(
    empresa?: EmpresaInfo | null,
    configOverrides?: Partial<DocumentConfig>,
    options?: { orientation?: 'p' | 'l' },
  ): Promise<PdfDocumentBuilder> {
    const config = { ...DEFAULT_CONFIG, ...configOverrides };
    const builder = new PdfDocumentBuilder(config, empresa || null, options?.orientation || 'p');

    if (config.mostrarLogo && empresa?.logo_url) {
      const fullLogoUrl = getImageUrl(empresa.logo_url);
      if (fullLogoUrl) {
        builder.logoBase64 = await loadImageAsBase64(fullLogoUrl);
      }
    }
    if (empresa?.sello_url) {
      const fullSelloUrl = getImageUrl(empresa.sello_url);
      if (fullSelloUrl) {
        builder.selloBase64 = await loadImageAsBase64(fullSelloUrl);
      }
    }
    return builder;
  }

  // ── Page management ────────────────────────────────────────────
  addPageBreakIfNeeded(spaceNeeded: number): boolean {
    if (this.y + spaceNeeded > this.pageHeight - 25) {
      this.doc.addPage();
      this.y = this.margin;
      this.pageCount++;
      return true;
    }
    return false;
  }

  addPage(): void {
    this.doc.addPage();
    this.y = this.margin;
    this.pageCount++;
  }

  /** Add a new page forcing landscape A4 orientation. */
  addLandscapePage(): void {
    this.doc.addPage('a4', 'l');
    this.y = this.margin;
    this.pageCount++;
  }

  /** Add a new page forcing portrait A4 orientation. */
  addPortraitPage(): void {
    this.doc.addPage('a4', 'p');
    this.y = this.margin;
    this.pageCount++;
  }

  // ── Header ─────────────────────────────────────────────────────
  addHeader(titulo: string, subtitulo?: string): void {
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);
    const accent = hexToRgb(this.config.colorAcento);

    let logoEndX = this.margin;

    // Logo
    if (this.logoBase64 && this.config.mostrarLogo) {
      try {
        const maxW = 40;
        const maxH = 22;
        let fmt: 'PNG' | 'JPEG' | 'WEBP' = 'PNG';
        const m = /^data:image\/(png|jpe?g|webp)/i.exec(this.logoBase64);
        if (m) {
          const t = m[1].toLowerCase();
          fmt = t === 'png' ? 'PNG' : t === 'webp' ? 'WEBP' : 'JPEG';
        }
        let drawW = maxW;
        let drawH = maxH;
        try {
          const props = (this.doc as any).getImageProperties(this.logoBase64);
          if (props?.width && props?.height) {
            const ratio = props.width / props.height;
            if (maxW / maxH > ratio) {
              drawH = maxH; drawW = maxH * ratio;
            } else {
              drawW = maxW; drawH = maxW / ratio;
            }
          }
        } catch { /* fallback */ }
        this.doc.addImage(this.logoBase64, fmt, this.margin, this.y - 2, drawW, drawH);
        logoEndX = this.margin + drawW + 5;
      } catch {
        // logo failed, skip
      }
    }


    // Company name + title
    const textX = logoEndX;
    if (this.config.mostrarDatosEmpresa && this.empresa) {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...secondary);
      this.doc.text(this.empresa.razon_social || this.empresa.nombre, textX, this.y + 5);
    }

    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...primary);
    this.doc.text(titulo.toUpperCase(), textX, this.y + 13);

    if (subtitulo) {
      this.doc.setFontSize(11);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...secondary);
      this.doc.text(subtitulo, textX, this.y + 19);
      this.y += 25;
    } else {
      this.y += 20;
    }

    // Accent line under header
    this.doc.setDrawColor(...accent);
    this.doc.setLineWidth(0.7);
    this.doc.line(this.margin, this.y, this.pageWidth - this.margin, this.y);
    this.doc.setLineWidth(0.2);
    this.y += 6;
  }

  // ── Section title ──────────────────────────────────────────────
  addSection(titulo: string): void {
    this.addPageBreakIfNeeded(15);
    const primary = hexToRgb(this.config.colorPrimario);
    const accent = hexToRgb(this.config.colorAcento);

    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...primary);
    this.doc.text(titulo.toUpperCase(), this.margin, this.y);
    this.y += 1;

    // Small accent underline
    this.doc.setDrawColor(...accent);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.margin, this.y, this.margin + 40, this.y);
    this.doc.setLineWidth(0.2);
    this.y += 6;
  }

  // ── Field (label: value) ───────────────────────────────────────
  addField(label: string, value: string): void {
    this.addPageBreakIfNeeded(7);
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);

    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...secondary);
    this.doc.text(`${label}:`, this.margin, this.y);

    const labelWidth = this.doc.getTextWidth(`${label}: `);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...primary);
    this.doc.text(value, this.margin + labelWidth + 1, this.y);
    this.y += 5;
  }

  // ── Fields grid ────────────────────────────────────────────────
  addFieldsGrid(fields: FieldItem[], columns = 2): void {
    const colWidth = this.contentWidth / columns;
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);

    for (let i = 0; i < fields.length; i += columns) {
      this.addPageBreakIfNeeded(7);
      for (let c = 0; c < columns && i + c < fields.length; c++) {
        const f = fields[i + c];
        const x = this.margin + c * colWidth;

        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(...secondary);
        this.doc.text(`${f.label}:`, x, this.y);

        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(...primary);
        this.doc.text(f.value, x + this.doc.getTextWidth(`${f.label}: `) + 1, this.y);
      }
      this.y += 5;
    }
    this.y += 2;
  }

  // ── Table ──────────────────────────────────────────────────────
  addTable(headers: string[], rows: string[][], colWidths?: number[]): void {
    const accent = hexToRgb(this.config.colorAcento);
    const primary = hexToRgb(this.config.colorPrimario);

    const widths = colWidths || headers.map(() => this.contentWidth / headers.length);

    this.addPageBreakIfNeeded(14);

    // Header row
    this.doc.setFillColor(accent[0], accent[1], accent[2]);
    this.doc.rect(this.margin, this.y, this.contentWidth, 7, 'F');
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);

    let xPos = this.margin;
    headers.forEach((h, i) => {
      this.doc.text(h, xPos + 2, this.y + 5);
      xPos += widths[i];
    });
    this.y += 8;

    // Data rows
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...primary);
    const rowBg1: [number, number, number] = [250, 250, 250];
    const rowBg2: [number, number, number] = [255, 255, 255];

    rows.forEach((row, rIdx) => {
      this.addPageBreakIfNeeded(7);
      const bg = rIdx % 2 === 0 ? rowBg1 : rowBg2;
      this.doc.setFillColor(...bg);
      this.doc.rect(this.margin, this.y - 1, this.contentWidth, 6, 'F');

      this.doc.setFontSize(8);
      let x = this.margin;
      row.forEach((cell, cIdx) => {
        const maxW = widths[cIdx] - 4;
        const truncated = cell.length > maxW / 2 ? cell.substring(0, Math.floor(maxW / 2)) + '…' : cell;
        this.doc.text(truncated, x + 2, this.y + 3);
        x += widths[cIdx];
      });
      this.y += 6;
    });

    this.y += 4;
  }

  // ── Multiline table: wraps cell text and grows row height dynamically ──
  addMultilineTable(
    headers: string[],
    rows: string[][],
    colWidths?: number[],
    opts?: { fontSize?: number; detailRows?: (string | null | undefined)[]; cellPadX?: number; cellPadY?: number },
  ): void {
    const accent = hexToRgb(this.config.colorAcento);
    const primary = hexToRgb(this.config.colorPrimario);
    const fontSize = opts?.fontSize ?? 8.5;
    const lineH = fontSize * 0.42;
    const cellPadX = opts?.cellPadX ?? 2;
    const cellPadY = opts?.cellPadY ?? 2;
    const detailFontSize = Math.max(7, fontSize - 0.5);
    const detailLineH = detailFontSize * 0.45;

    const widths = colWidths || headers.map(() => this.contentWidth / headers.length);
    const totalW = widths.reduce((a, b) => a + b, 0);

    // Pre-wrap headers so long titles don't overlap
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'bold');
    const wrappedHeaders = headers.map((h, i) =>
      this.doc.splitTextToSize(sanitizePdfText(h || ''), widths[i] - cellPadX * 2) as string[]
    );
    const headerLines = Math.max(1, ...wrappedHeaders.map(l => l.length));
    const headerH = Math.max(7, headerLines * lineH + cellPadY * 2);

    this.addPageBreakIfNeeded(headerH + 7);

    const drawHeader = () => {
      this.doc.setFillColor(accent[0], accent[1], accent[2]);
      this.doc.rect(this.margin, this.y, totalW, headerH, 'F');
      this.doc.setFontSize(fontSize);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(255, 255, 255);
      let xh = this.margin;
      wrappedHeaders.forEach((lines, i) => {
        let ty = this.y + cellPadY + lineH * 0.85;
        lines.forEach(ln => {
          this.doc.text(ln, xh + cellPadX, ty);
          ty += lineH;
        });
        xh += widths[i];
      });
      this.y += headerH;
    };

    drawHeader();

    // Data rows
    this.doc.setFont('helvetica', 'normal');
    const rowBg1: [number, number, number] = [250, 250, 250];
    const rowBg2: [number, number, number] = [255, 255, 255];
    const detailBg: [number, number, number] = [243, 246, 250];

    rows.forEach((row, rIdx) => {
      const wrapped = row.map((cell, cIdx) =>
        this.doc.splitTextToSize(sanitizePdfText(cell || ''), widths[cIdx] - cellPadX * 2) as string[]
      );
      const maxLines = Math.max(1, ...wrapped.map(l => l.length));
      const rowH = maxLines * lineH + cellPadY * 2;

      // Optional detail row (full-width colspan) below this element
      const detailText = opts?.detailRows?.[rIdx];
      let detailWrapped: string[] = [];
      let detailH = 0;
      if (detailText && detailText.trim()) {
        this.doc.setFontSize(detailFontSize);
        this.doc.setFont('helvetica', 'normal');
        detailWrapped = this.doc.splitTextToSize(
          sanitizePdfText(detailText),
          totalW - cellPadX * 2,
        ) as string[];
        detailH = detailWrapped.length * detailLineH + cellPadY * 2;
      }

      if (this.y + rowH + detailH > this.pageHeight - this.margin - 10) {
        this.addPage();
        drawHeader();
        this.doc.setFont('helvetica', 'normal');
      }

      const bg = rIdx % 2 === 0 ? rowBg1 : rowBg2;
      this.doc.setFillColor(...bg);
      this.doc.rect(this.margin, this.y, totalW, rowH, 'F');

      this.doc.setFontSize(fontSize);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...primary);
      let x = this.margin;
      wrapped.forEach((lines, cIdx) => {
        let ty = this.y + cellPadY + lineH * 0.85;
        lines.forEach(ln => {
          this.doc.text(ln, x + cellPadX, ty);
          ty += lineH;
        });
        x += widths[cIdx];
      });
      this.y += rowH;

      if (detailH > 0) {
        this.doc.setFillColor(...detailBg);
        this.doc.rect(this.margin, this.y, totalW, detailH, 'F');
        this.doc.setFontSize(detailFontSize);
        this.doc.setTextColor(...primary);
        let dy = this.y + cellPadY + detailLineH * 0.85;
        detailWrapped.forEach(ln => {
          this.doc.text(ln, this.margin + cellPadX, dy);
          dy += detailLineH;
        });
        this.y += detailH;
      }
    });

    this.y += 4;
  }



  // ── Signature block ────────────────────────────────────────────
  addSignatureBlock(firmas: SignatureData[]): void {
    this.addPageBreakIfNeeded(50);
    this.addSection('Firmas');

    const secondary = hexToRgb(this.config.colorSecundario);
    const minSpacing = 8;
    const sigHeight = 25;
    let sigWidth = 65;
    const required = firmas.length * sigWidth + (firmas.length + 1) * minSpacing;
    if (required > this.contentWidth) {
      sigWidth = Math.floor((this.contentWidth - (firmas.length + 1) * minSpacing) / firmas.length);
    }
    const spacing = (this.contentWidth - firmas.length * sigWidth) / (firmas.length + 1);

    firmas.forEach((firma, i) => {
      const x = this.margin + spacing + i * (sigWidth + spacing);

      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(...secondary);
      this.doc.text(firma.label, x, this.y);

      const effectiveImage = firma.imageData
        || (firma.esEmpresa && this.selloBase64 ? this.selloBase64 : null);

      if (effectiveImage) {
        try {
          this.doc.addImage(effectiveImage, 'PNG', x, this.y + 2, sigWidth, sigHeight);
        } catch {
          this.doc.setDrawColor(...secondary);
          this.doc.rect(x, this.y + 2, sigWidth, sigHeight);
          this.doc.setFontSize(8);
          this.doc.setFont('helvetica', 'normal');
          this.doc.text('(Firma no disponible)', x + 5, this.y + 15);
        }
      } else {
        this.doc.setDrawColor(...secondary);
        this.doc.rect(x, this.y + 2, sigWidth, sigHeight);
        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'normal');
        this.doc.text('(Sin firma)', x + 15, this.y + 15);
      }

      if (firma.nombre) {
        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(...secondary);
        this.doc.text(firma.nombre, x, this.y + sigHeight + 6);
      }
    });

    this.y += sigHeight + 12;
  }

  // ── Legal text ─────────────────────────────────────────────────
  addLegalText(texto: string): void {
    this.addPageBreakIfNeeded(20);
    const secondary = hexToRgb(this.config.colorSecundario);

    this.doc.setFontSize(7);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...secondary);
    const lines = this.doc.splitTextToSize(texto, this.contentWidth);
    this.doc.text(lines, this.margin, this.y);
    this.y += lines.length * 3 + 4;
  }

  // ── Wrapped text block ─────────────────────────────────────────
  addText(text: string, fontSize = 10): void {
    this.addPageBreakIfNeeded(10);
    const primary = hexToRgb(this.config.colorPrimario);

    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...primary);
    const lines = this.doc.splitTextToSize(text, this.contentWidth);
    lines.forEach((line: string) => {
      this.addPageBreakIfNeeded(fontSize * 0.4 + 1);
      this.doc.text(line, this.margin, this.y);
      this.y += fontSize * 0.4;
    });
    this.y += 4;
  }

  // ── Footer (call on every page after content) ──────────────────
  addFooter(): void {
    const totalPages = this.doc.getNumberOfPages();
    const secondary = hexToRgb(this.config.colorSecundario);
    const accent = hexToRgb(this.config.colorAcento);

    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      const footerY = this.pageHeight - 12;

      // Accent line
      this.doc.setDrawColor(...accent);
      this.doc.setLineWidth(0.3);
      this.doc.line(this.margin, footerY, this.pageWidth - this.margin, footerY);

      this.doc.setFontSize(7);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...secondary);

      // Left: company info or custom text
      const leftText = this.config.textoPiePagina
        || (this.config.mostrarDatosEmpresa && this.empresa
          ? [
              this.empresa.nombre,
              this.empresa.nif_cif ? `NIF/CIF: ${this.empresa.nif_cif}` : '',
              this.empresa.numero_dgp ? `DGP: ${this.empresa.numero_dgp}` : '',
              this.empresa.numero_reci ? `RECI: ${this.empresa.numero_reci}` : '',
              this.empresa.telefono || '',
            ].filter(Boolean).join(' | ')
          : '');

      if (leftText) {
        this.doc.text(leftText, this.margin, footerY + 4);
      }

      // Right: date + page
      const fecha = new Date().toLocaleDateString('es-ES');
      this.doc.text(`${fecha} — Pág. ${i}/${totalPages}`, this.pageWidth - this.margin, footerY + 4, { align: 'right' });
    }
  }

  // ── Spacer ─────────────────────────────────────────────────────
  addSpacer(mm = 5): void {
    this.y += mm;
  }

  // ── Branded header (band with logo + title + chips) ────────────
  addBrandedHeader(opts: { titulo: string; numero?: string; estado?: string; fechaEmision?: string }): void {
    const accent = hexToRgb(this.config.colorAcento);
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);

    const bandH = 26;
    // Soft accent band
    this.doc.setFillColor(accent[0], accent[1], accent[2]);
    this.doc.rect(0, 0, this.pageWidth, bandH, 'F');

    let textX = this.margin;

    // Logo on white card to the left, on top of band — preserva aspect-ratio y detecta formato
    if (this.logoBase64 && this.config.mostrarLogo) {
      try {
        const maxW = 34;
        const maxH = 18;
        // Detect format from data URL
        let fmt: 'PNG' | 'JPEG' | 'WEBP' = 'PNG';
        const m = /^data:image\/(png|jpe?g|webp)/i.exec(this.logoBase64);
        if (m) {
          const t = m[1].toLowerCase();
          fmt = t === 'png' ? 'PNG' : t === 'webp' ? 'WEBP' : 'JPEG';
        }
        // Get natural dimensions to preserve ratio
        let drawW = maxW;
        let drawH = maxH;
        try {
          const props = (this.doc as any).getImageProperties(this.logoBase64);
          if (props?.width && props?.height) {
            const ratio = props.width / props.height;
            // Fit inside (maxW, maxH)
            if (maxW / maxH > ratio) {
              drawH = maxH;
              drawW = maxH * ratio;
            } else {
              drawW = maxW;
              drawH = maxW / ratio;
            }
          }
        } catch { /* fallback to maxW/maxH */ }

        const pad = 2;
        const cardW = drawW + pad * 2;
        const cardH = drawH + pad * 2;
        const cardX = this.margin;
        const cardY = (26 - cardH) / 2; // centrado vertical en la banda
        const logoX = cardX + pad;
        const logoY = cardY + pad;

        this.doc.setFillColor(255, 255, 255);
        this.doc.roundedRect(cardX, cardY, cardW, cardH, 2, 2, 'F');
        this.doc.addImage(this.logoBase64, fmt, logoX, logoY, drawW, drawH);
        textX = cardX + cardW + 6;

      } catch {
        // ignore
      }
    }

    // Reserva el área derecha para nº de contrato / fecha y calcula ancho disponible para el título
    const rightReserved = 50; // mm reservados para numero+fecha a la derecha
    const titleMaxW = Math.max(20, this.pageWidth - this.margin - rightReserved - textX - 4);
    const titleText = opts.titulo.toUpperCase();

    // Ajuste automático del tamaño del título para que quepa
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFont('helvetica', 'bold');
    let titleSize = 15;
    this.doc.setFontSize(titleSize);
    while (this.doc.getTextWidth(titleText) > titleMaxW && titleSize > 8) {
      titleSize -= 0.5;
      this.doc.setFontSize(titleSize);
    }
    this.doc.text(titleText, textX, 14);

    if (this.empresa && this.config.mostrarDatosEmpresa) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8.5);
      const empresaName = this.empresa.razon_social || this.empresa.nombre || '';
      // Truncar nombre si excede el ancho disponible
      let nameToDraw = empresaName;
      while (this.doc.getTextWidth(nameToDraw) > titleMaxW && nameToDraw.length > 4) {
        nameToDraw = nameToDraw.slice(0, -2);
      }
      if (nameToDraw !== empresaName) nameToDraw = nameToDraw.slice(0, -1) + '…';
      this.doc.text(nameToDraw, textX, 21);
    }

    // Right side: numero + fecha
    if (opts.numero) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(11);
      this.doc.setTextColor(255, 255, 255);
      this.doc.text(opts.numero, this.pageWidth - this.margin, 12, { align: 'right' });
    }
    if (opts.fechaEmision) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8.5);
      this.doc.text(opts.fechaEmision, this.pageWidth - this.margin, 18, { align: 'right' });
    }
    if (opts.estado) {
      // Estado as chip below
      this.addChip(opts.estado, this.pageWidth - this.margin - 30, 21.5, 30, 'inverse');
    }


    this.y = bandH + 8;

    // Sub-row with company contact
    if (this.empresa && this.config.mostrarDatosEmpresa) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.doc.setTextColor(...secondary);
      const parts: string[] = [];
      if (this.empresa.nif_cif) parts.push(`NIF/CIF: ${this.empresa.nif_cif}`);
      const dir = [this.empresa.direccion_calle, this.empresa.direccion_numero].filter(Boolean).join(' ');
      if (dir) parts.push(dir);
      const loc = [this.empresa.codigo_postal, this.empresa.ciudad].filter(Boolean).join(' ');
      if (loc) parts.push(loc);
      if (this.empresa.telefono) parts.push(this.empresa.telefono);
      if (this.empresa.email) parts.push(this.empresa.email);
      if (this.empresa.numero_dgp) parts.push(`Nº DGP: ${this.empresa.numero_dgp}`);
      if (this.empresa.numero_reci) parts.push(`Nº RECI: ${this.empresa.numero_reci}`);
      if (parts.length > 0) {
        this.doc.text(parts.join('  ·  '), this.margin, this.y);
        this.y += 5;
      }
    }
    this.y += 2;
  }

  // ── Chip / pill ────────────────────────────────────────────────
  addChip(text: string, x: number, y: number, width = 0, variant: 'accent' | 'inverse' | 'muted' = 'accent'): void {
    const accent = hexToRgb(this.config.colorAcento);
    const secondary = hexToRgb(this.config.colorSecundario);

    this.doc.setFontSize(7.5);
    this.doc.setFont('helvetica', 'bold');
    const w = width || this.doc.getTextWidth(text.toUpperCase()) + 6;
    const h = 5;

    if (variant === 'inverse') {
      this.doc.setFillColor(255, 255, 255);
      this.doc.roundedRect(x, y - h + 1, w, h, 1.5, 1.5, 'F');
      this.doc.setTextColor(accent[0], accent[1], accent[2]);
    } else if (variant === 'muted') {
      this.doc.setFillColor(235, 235, 238);
      this.doc.roundedRect(x, y - h + 1, w, h, 1.5, 1.5, 'F');
      this.doc.setTextColor(...secondary);
    } else {
      this.doc.setFillColor(accent[0], accent[1], accent[2]);
      this.doc.roundedRect(x, y - h + 1, w, h, 1.5, 1.5, 'F');
      this.doc.setTextColor(255, 255, 255);
    }
    this.doc.text(text.toUpperCase(), x + w / 2, y - 0.5, { align: 'center' });
  }

  // ── Info card (titled box with rows) ───────────────────────────
  addInfoCard(title: string, fields: FieldItem[], opts?: { x?: number; width?: number; minHeight?: number }): number {
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);
    const accent = hexToRgb(this.config.colorAcento);

    const x = opts?.x ?? this.margin;
    const w = opts?.width ?? this.contentWidth;
    const padX = 4;
    const padY = 5;
    const rowH = 5;
    const titleH = 6;
    const innerH = fields.length * rowH;
    const h = Math.max(opts?.minHeight ?? 0, padY * 2 + titleH + innerH);

    if (opts?.x === undefined) this.addPageBreakIfNeeded(h + 4);

    // Background
    this.doc.setFillColor(247, 247, 249);
    this.doc.setDrawColor(225, 225, 230);
    this.doc.setLineWidth(0.2);
    this.doc.roundedRect(x, this.y, w, h, 1.5, 1.5, 'FD');

    // Title
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(accent[0], accent[1], accent[2]);
    this.doc.text(title.toUpperCase(), x + padX, this.y + padY + 2);

    // Fields
    let cy = this.y + padY + titleH + 2;
    fields.forEach((f) => {
      this.doc.setFontSize(7.5);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...secondary);
      this.doc.text(`${f.label}:`, x + padX, cy);
      const lblW = this.doc.getTextWidth(`${f.label}: `);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(...primary);
      const val = this.doc.splitTextToSize(f.value, w - padX * 2 - lblW - 2);
      this.doc.text(val[0] || '', x + padX + lblW + 1, cy);
      cy += rowH;
    });

    if (opts?.x === undefined) this.y += h + 4;
    return h;
  }

  // ── Two info cards side by side ───────────────────────────────
  addInfoCardRow(left: { title: string; fields: FieldItem[] }, right: { title: string; fields: FieldItem[] }): void {
    const gap = 4;
    const w = (this.contentWidth - gap) / 2;
    const padY = 5, titleH = 6, rowH = 5;
    const hL = padY * 2 + titleH + left.fields.length * rowH;
    const hR = padY * 2 + titleH + right.fields.length * rowH;
    const h = Math.max(hL, hR);
    this.addPageBreakIfNeeded(h + 4);
    const startY = this.y;
    this.addInfoCard(left.title, left.fields, { x: this.margin, width: w, minHeight: h });
    this.y = startY;
    this.addInfoCard(right.title, right.fields, { x: this.margin + w + gap, width: w, minHeight: h });
    this.y = startY + h + 4;
  }

  // ── Quote-style block (vertical accent rule + text) ───────────
  addQuoteBlock(text: string): void {
    const accent = hexToRgb(this.config.colorAcento);
    const primary = hexToRgb(this.config.colorPrimario);
    const lineH = 4.2;
    const padTop = 4;
    const padBottom = 3;
    const textX = this.margin + 6;
    const maxTextW = this.contentWidth - 8;
    const bottomY = this.pageHeight - 25;

    this.doc.setFontSize(9.5);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...primary);
    const lines = this.doc.splitTextToSize(sanitizePdfText(text), maxTextW) as string[];
    let index = 0;

    while (index < Math.max(lines.length, 1)) {
      if (this.y + padTop + lineH + padBottom > bottomY) {
        this.addPage();
      }

      const availableH = bottomY - this.y;
      const maxLines = Math.max(1, Math.floor((availableH - padTop - padBottom) / lineH));
      const chunk = lines.slice(index, index + maxLines);
      const h = chunk.length * lineH + padTop + padBottom;

      this.doc.setFillColor(accent[0], accent[1], accent[2]);
      this.doc.rect(this.margin, this.y, 2, h, 'F');
      this.doc.text(chunk.length ? chunk : [''], textX, this.y + padTop);

      this.y += h + 3;
      index += chunk.length || 1;
    }
  }

  // ── Totals box (invoice-style, right aligned) ─────────────────
  addTotalsBox(items: { label: string; value: string }[], total: { label: string; value: string }): void {
    const accent = hexToRgb(this.config.colorAcento);
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);

    const w = 80;
    const x = this.pageWidth - this.margin - w;
    const rowH = 6;
    const h = items.length * rowH + 9;
    this.addPageBreakIfNeeded(h + 4);

    this.doc.setDrawColor(225, 225, 230);
    this.doc.setLineWidth(0.2);
    this.doc.roundedRect(x, this.y, w, h, 1.5, 1.5, 'S');

    let cy = this.y + 4.5;
    this.doc.setFontSize(9);
    items.forEach((it) => {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...secondary);
      this.doc.text(it.label, x + 3, cy);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...primary);
      this.doc.text(it.value, x + w - 3, cy, { align: 'right' });
      cy += rowH;
    });

    // Total band
    this.doc.setFillColor(accent[0], accent[1], accent[2]);
    this.doc.rect(x, cy - 1, w, 9, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(10);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(total.label.toUpperCase(), x + 3, cy + 5);
    this.doc.text(total.value, x + w - 3, cy + 5, { align: 'right' });

    this.y += h + 5;
  }

  // ── Company details block ──────────────────────────────────────
  addEmpresaDetails(): void {
    if (!this.empresa || !this.config.mostrarDatosEmpresa) return;
    const e = this.empresa;
    const fields: FieldItem[] = [];

    if (e.nif_cif) fields.push({ label: 'NIF/CIF', value: e.nif_cif });
    const dir = [e.direccion_calle, e.direccion_numero].filter(Boolean).join(' ');
    if (dir) fields.push({ label: 'Dirección', value: dir });
    const loc = [e.codigo_postal, e.ciudad, e.provincia].filter(Boolean).join(' ');
    if (loc) fields.push({ label: 'Localidad', value: loc });
    if (e.telefono) fields.push({ label: 'Teléfono', value: e.telefono });
    if (e.email) fields.push({ label: 'Email', value: e.email });

    if (fields.length > 0) {
      this.addFieldsGrid(fields, 2);
    }
  }

  // ── Block subtitle (smaller than addSection) ───────────────────
  addBlockSubtitle(titulo: string): void {
    this.addPageBreakIfNeeded(10);
    const secondary = hexToRgb(this.config.colorSecundario);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...secondary);
    this.doc.text(titulo.toUpperCase(), this.margin, this.y);
    this.y += 4.5;
  }

  // ── Checklist question line ────────────────────────────────────
  addChecklistItem(opts: {
    code?: string;
    text: string;
    badge: string;
    badgeTone?: 'ok' | 'ko' | 'na' | 'neutral';
    observaciones?: string;
  }): void {
    const primary = hexToRgb(this.config.colorPrimario);
    const secondary = hexToRgb(this.config.colorSecundario);
    const tone = opts.badgeTone || 'neutral';
    const toneColors: Record<string, [number, number, number]> = {
      ok: [34, 139, 70],
      ko: [200, 50, 50],
      na: [120, 120, 120],
      neutral: [120, 120, 120],
    };
    const badgeColor = toneColors[tone];

    const badgeText = sanitizePdfText(opts.badge || '-');
    const badgeW = 14;
    const badgeH = 5;

    this.doc.setFontSize(8.5);
    this.doc.setFont('helvetica', 'normal');
    const codePart = opts.code ? `${sanitizePdfText(opts.code)}  ` : '';
    const fullText = `${codePart}${sanitizePdfText(opts.text || '')}`;
    const textWidth = this.contentWidth - badgeW - 4;
    const lines = this.doc.splitTextToSize(fullText, textWidth);
    const lineH = 4.2;
    const textTop = 3;
    const blockH = Math.max(badgeH + 2, textTop + lines.length * lineH) + 1.5;


    this.addPageBreakIfNeeded(blockH + (opts.observaciones ? 5 : 0));

    // Soft separator
    this.doc.setDrawColor(235, 235, 240);
    this.doc.setLineWidth(0.2);
    this.doc.line(this.margin, this.y - 0.5, this.pageWidth - this.margin, this.y - 0.5);

    // Text
    this.doc.setTextColor(...primary);
    lines.forEach((ln: string, idx: number) => {
      this.doc.text(ln, this.margin + 1, this.y + textTop + idx * lineH);
    });


    // Badge (top right of the row)
    this.doc.setFillColor(badgeColor[0], badgeColor[1], badgeColor[2]);
    this.doc.roundedRect(this.pageWidth - this.margin - badgeW, this.y + 0.3, badgeW, badgeH, 1, 1, 'F');
    this.doc.setFontSize(7.5);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(badgeText.toUpperCase(), this.pageWidth - this.margin - badgeW / 2, this.y + 3.7, { align: 'center' });

    this.y += blockH;

    if (opts.observaciones) {
      this.doc.setFontSize(7.5);
      this.doc.setFont('helvetica', 'italic');
      this.doc.setTextColor(...secondary);
      const obsLines = this.doc.splitTextToSize(sanitizePdfText(`Obs: ${opts.observaciones}`), this.contentWidth - 4);
      obsLines.forEach((l: string) => {
        this.addPageBreakIfNeeded(4);
        this.doc.text(l, this.margin + 3, this.y + 2.5);
        this.y += 3.4;
      });
      this.y += 1;
    }
  }

  // ── Save ───────────────────────────────────────────────────────
  save(fileName: string): void {
    this.addFooter();
    this.doc.save(fileName);
  }

  /** Return the jsPDF instance (after footer) for custom processing */
  finalize(): jsPDF {
    this.addFooter();
    return this.doc;
  }
}

